import classNames from 'classnames'
import plur from 'plur'
import {memo, useCallback, useMemo} from 'react'

import EntityPath from '../../../containers/EntityPath'
import TransformText from '../../../containers/TransformText'
import {ResolutionType, InvestigationStates, stringifyId} from '../../../types'
import type {BuildTypeId, ProjectId, Investigation, UserId} from '../../../types'
import {parseDateToObj} from '../../../utils/dateTimeUtils'
import Icon from '../Icon/Icon'

import {getAssigneeName, getAssignmentName} from './Investigations.utils'

import styles from './Investigations.css'

type Props = {
  className?: string
  buildTypeId?: BuildTypeId | null | undefined
  investigation: Investigation
  currentUserId?: UserId | null | undefined
  isAutodetectTimeZone?: boolean
  highlightAssignedToMe?: boolean
}

function InvestigationDetails({
  className,
  buildTypeId,
  currentUserId,
  investigation,
  isAutodetectTimeZone,
  highlightAssignedToMe,
}: Props) {
  const {state, assignment, assignee, resolution, scope} = investigation

  const isAssignedToCurrentUser = assignee.id === currentUserId
  const assigneeName = getAssigneeName(investigation, isAssignedToCurrentUser)
  const assignmentName = getAssignmentName(investigation)

  const scopeList = []
  const scopeProjectId = scope?.project?.id
  if (scopeProjectId != null) {
    scopeList.push({projectId: scopeProjectId})
  }

  const scopeBuildTypes = scope?.buildTypes?.buildType ?? []
  scopeBuildTypes.forEach(buildType => {
    scopeList.push({buildTypeId: buildType.id})
  })

  const timestampText = parseDateToObj(assignment.timestamp, isAutodetectTimeZone).format(
    'D MMM YY LT',
  )

  const resolutionWay = useMemo(() => {
    switch (resolution.type) {
      case ResolutionType.MANUALLY:
        return 'manually'
      case ResolutionType.WHEN_FIXED:
        return 'automatically when fixed'
      default:
        return null
    }
  }, [resolution.type])

  const renderEntityPath = useCallback(
    (data: {projectId?: ProjectId; buildTypeId?: BuildTypeId}) => {
      const key = `investigation_${stringifyId(data.projectId) ?? stringifyId(data.buildTypeId)}`
      return (
        <EntityPath
          key={key}
          className={styles.entityPath}
          linkClassName={styles.entityPathLink}
          projectOrBuildTypeNode={{nodeType: 'all'}}
          withLeafStatusIcon={false}
          withCollapsing
          {...data}
        />
      )
    },
    [],
  )

  return (
    <div className={className}>
      <div className={styles.state} data-test-investigation-state>
        {state === InvestigationStates.FIXED ? (
          <span>{`Investigation fixed by ${assigneeName}`}</span>
        ) : (
          <span>
            {`Investigation assigned to `}
            {highlightAssignedToMe && isAssignedToCurrentUser && (
              <Icon
                className={classNames(styles.icon, styles.assignedToMe)}
                name="investigation"
                aria-label="investigation"
                title=""
              />
            )}
            <span
              className={classNames(styles.assigneeName, {
                [styles.highlight]: highlightAssignedToMe && isAssignedToCurrentUser,
              })}
            >
              {` ${assigneeName}`}
            </span>
          </span>
        )}
        <span className={styles.timestamp}>{` ${timestampText}`}</span>
        {assignmentName
          ? ` (${state === InvestigationStates.TAKEN ? 'assigned' : 'done'} by ${assignmentName})`
          : null}
        {resolutionWay ? `. Resolve ${resolutionWay}` : null}
      </div>
      {scopeList.length > 0 && (
        <div className={styles.scope} data-test-investigation-scope>
          <span className={styles.scopeTitle}>{`${plur('Scope', scopeList.length)}: `}</span>
          <div className={styles.scopeList}>{scopeList.map(item => renderEntityPath(item))}</div>
        </div>
      )}
      {assignment.text != null && assignment.text !== '' && (
        <div data-test-investigation-comment-text className={styles.comment}>
          <span>{'Comment: '}</span>
          <TransformText
            className={styles.commentText}
            loaderClassName={styles.commentTextLoader}
            buildTypeId={buildTypeId}
            text={assignment.text}
            lineHeight={20}
            TagName="span"
            showPlaceholder
          />
        </div>
      )}
    </div>
  )
}

export default memo(InvestigationDetails)
